import React, { useEffect, useState } from 'react';
import Accordion from '../../../../components/Accordion';
import { Button } from '../../../../components';
import Loading from '../../../../components/Loading';
import { ModalDatePicker } from '../../../../components/ModalDatePicker';
import { PatientTime } from '../PatientTime';
import { CalendarSchedule } from '../CalendarSchedule';

import { ExecutiveCheckupIcon } from '../../../../components/Icons/Services/ExecutiveCheckup';
import { HeadProduct } from '../../../../components/HeadProduct';
import { SlotsUnavailableWrapper } from './styles';

export type ProductSlotsProps = {
  productName: string;
  fixed?: boolean;
  exclude?: { date: string; hour: string }[];
  check?: boolean;
  onSubmit?: (slot: DateSlots) => void;
  onValueChange?: (slot?: DateSlots) => void;
  onError?: (error: Error) => void;
  showScheduleButton?: boolean;
  visible?: boolean;
  toggleVisible: () => void;
  hour?: DateSlots;
  handleWithoutSlots: () => void;

  loadSlotsDates: (date: Date) => Promise<DateSlots[]>;
  loadSlotsHours: (date: Date) => Promise<DateSlots[]>;

  showProductInfo?: boolean;

  customHeadAccordion?: React.ReactNode;

  serviceName?: string;
  showDates?: boolean;

  loading?: boolean;
  externalDisplayDates?: DateSlots[];
};

export type DateSlots = {
  id: string;
  date: string;
  slot: any;
};

export function ProductSlots({
  productName,
  fixed,
  exclude,
  check,
  onSubmit,
  onValueChange,
  onError,
  showScheduleButton,
  visible,
  toggleVisible,
  hour,
  handleWithoutSlots,
  showProductInfo,
  serviceName,

  loadSlotsDates,
  loadSlotsHours,
  customHeadAccordion,
  showDates = true,
  loading = false,
  externalDisplayDates
}: ProductSlotsProps) {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [datesLoading, setDatesLoading] = useState(true);
  const [hoursLoading, setHoursLoading] = useState(false);

  const [displayDates, setDisplayDates] = React.useState<Date[]>([]);
  const [displayDatesPicker, setDisplayDatesPicker] = React.useState<Date[]>([]);

  const [selectedDate, setSelectedDate] = useState<Date | null>(null);

  const [hours, setHours] = useState<DateSlots[]>([]);
  const [selectedHour, setSelectedHour] = useState<DateSlots | undefined>(hour);

  React.useEffect(() => {
    if (externalDisplayDates?.length) setDisplayDatesPicker(mapDates(externalDisplayDates));
  }, [externalDisplayDates]);

  const getDates = async (baseDate: Date) => {
    setSelectedHour(undefined);

    // if (!datesLoading) {
    setDatesLoading(true);

    const dates = await loadSlotsDates(baseDate);

    setDisplayDatesPicker(mapDates(dates));

    setDatesLoading(false);
    // }
  };

  const getHours = async () => {
    if (!selectedDate) return;

    setHoursLoading(true);

    const slotsHours = await loadSlotsHours(selectedDate);

    setHours(slotsHours ?? []);

    setSelectedHour(undefined);

    setHoursLoading(false);
  };

  useEffect(() => {
    if (selectedDate) {
      getHours();
    }
  }, [selectedDate]);

  useEffect(() => {
    if (!selectedDate) {
      setSelectedDate(displayDatesPicker[0]);
      setDisplayDates(displayDatesPicker);
    }
  }, [displayDatesPicker]);

  useEffect(() => {
    onValueChange?.(selectedHour);
  }, [selectedHour]);

  useEffect(() => {
    getDates(new Date());
  }, []);

  const mapDates = (slots: DateSlots[]): Date[] => {
    return slots.map(item => new Date(item.date + 'T00:00:00'));
  };

  return (
    <Accordion
      handleOpen={toggleVisible}
      isOpen={!!visible}
      fixed={fixed}
      externalContent={
        customHeadAccordion ? (
          customHeadAccordion
        ) : serviceName ? (
          <HeadProduct name={productName} description={serviceName} productIcon={<ExecutiveCheckupIcon />} />
        ) : null
      }
    >
      {showDates && (
        <>
          {!datesLoading && displayDates.length > 0 && (
            <CalendarSchedule
              displayDates={displayDates}
              selectedDate={selectedDate}
              onSelectDate={(date: Date) => setSelectedDate(date)}
              handleClickSeeMore={() => setIsDialogOpen(true)}
            />
          )}
        </>
      )}

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Loading loading={datesLoading || hoursLoading || loading} size={60} color={'#004f92'} />
      </div>

      {!loading && !datesLoading && !hoursLoading && hours.length === 0 && (
        <SlotsUnavailableWrapper>Sem horários disponíveis</SlotsUnavailableWrapper>
      )}

      {!hoursLoading && <PatientTime times={hours} selectedTime={selectedHour} onSelectedTime={setSelectedHour} />}

      {hours.length > 1 && showScheduleButton && (
        <Button title={'Agendar'} disabled={!selectedHour} onClick={() => selectedHour && onSubmit?.(selectedHour)} />
      )}

      <ModalDatePicker
        selectedDate={selectedDate}
        setSelectedDate={date => date && setSelectedDate(date)}
        displayDates={displayDates}
        setDisplayDates={setDisplayDates}
        isOpen={isDialogOpen}
        setIsOpen={setIsDialogOpen}
        displayDatesPicker={displayDatesPicker}
        onMonthChange={getDates}
        loading={datesLoading}
        loadDates={getDates}
      />
    </Accordion>
  );
}
